import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UnassignedSpendingService } from '@/services/UnassignedSpendingService';
import { useAuth } from '@/contexts/AuthContext';
import { budgetKeys } from '@/hooks/useBudgets';
import { toast } from 'sonner';
import type { UnassignedSpendingAssignRequest } from '@/types';

export const unassignedSpendingKeys = {
  all: ['unassigned-spending'] as const,
  list: () => [...unassignedSpendingKeys.all, 'list'] as const,
};

export function useUnassignedSpending() {
  const { user } = useAuth();

  return useQuery({
    queryKey: unassignedSpendingKeys.list(),
    queryFn: () => UnassignedSpendingService.list(),
    enabled: !!user,
    staleTime: 15 * 1000,
  });
}

export function useAssignUnassignedSpending() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UnassignedSpendingAssignRequest) =>
      UnassignedSpendingService.assign(request),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: unassignedSpendingKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      const skipped = result.skippedIds.filter((s) => s.reason === 'DATE_OUTSIDE_BUDGET').length;
      if (result.assignedIds.length > 0 && skipped > 0) {
        toast.success(
          `Assigned ${result.assignedIds.length}; ${skipped} stayed unassigned (outside period)`
        );
      } else if (result.assignedIds.length > 0) {
        toast.success(
          `Assigned ${result.assignedIds.length} purchase${result.assignedIds.length === 1 ? '' : 's'}`
        );
      } else if (skipped > 0) {
        toast.error('No purchases fit that budget period');
      }
    },
    onError: () => {
      toast.error('Failed to assign purchases. Please try again.');
    },
  });
}

export function useDiscardUnassignedSpending() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => UnassignedSpendingService.discard(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: unassignedSpendingKeys.all });
      toast.success('Discarded selected purchases');
    },
    onError: () => {
      toast.error('Failed to discard purchases. Please try again.');
    },
  });
}
